import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { InputComponent } from '../../input/input.component';
import { ButtonComponent } from '../../button/button.component';
import { CardComponent } from '../../card/card.component';
import { AuthService } from '../../../services/auth.service';
import { LanguageRoutingService } from '../../../services/language-routing.service';
import { RouterModule } from '@angular/router';
import { PasswordStrengthComponent } from '../../password-strength/password-strength.component';
import { AlertCardComponent } from '../../alert-card/alert-card.component';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    InputComponent,
    ButtonComponent,
    CardComponent,
    RouterModule,
    PasswordStrengthComponent,
    AlertCardComponent,
  ],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
})
export class ResetPasswordComponent implements OnInit {
  resetPasswordForm: FormGroup;
  isSubmitting = false;
  isSuccess = false;
  errorMessage = '';
  token: string | null = null;
  showPassword = false;
  showConfirmPassword = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private route: ActivatedRoute,
    public languageRoutingService: LanguageRoutingService,
    private transloco: TranslocoService
  ) {
    this.resetPasswordForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(6), PasswordStrengthComponent.passwordStrengthValidator]],
        confirmPassword: ['', [Validators.required]],
      },
      {
        validators: this.passwordMatchValidator,
      }
    );
  }

  ngOnInit(): void {
    // Get token from query params
    this.route.queryParams.subscribe((params) => {
      this.token = params['token'];

      // Validate token
      if (this.token) {
        this.authService.validateResetToken(this.token).subscribe({
          next: () => {
            // Token is valid, user can proceed with password reset
          },
          error: (_error) => {
            this.transloco.selectTranslate('auth.resetPassword.invalidToken').subscribe((message: string) => {
              this.errorMessage = message;
            });
          },
        });
      } else {
        this.transloco.selectTranslate('auth.resetPassword.noToken').subscribe((message: string) => {
          this.errorMessage = message;
        });
      }
    });
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;

    if (password !== confirmPassword) {
      form.get('confirmPassword')?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    return null;
  }

  onSubmit(): void {
    if (this.resetPasswordForm.invalid || !this.token) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const password = this.resetPasswordForm.get('password')?.value;

    this.authService
      .resetPassword(this.token, password)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: () => {
          this.isSuccess = true;
        },
        error: (_error) => {
          this.transloco.selectTranslate('auth.resetPassword.error').subscribe((message: string) => {
            this.errorMessage = message;
          });
        },
      });
  }

  // Toggle password visibility
  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  // Toggle confirm password visibility
  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
}
